// Hangman interface: a big on-screen alphabet (a letter key on a keyboard works too), the word as large blanks,
// the theme as a clue, and the drawing filling in with each wrong guess. Hint reveals a letter. New word takes
// the next word from the bank. No undo: a guess is a guess. The daily is one word per date for everyone.
import { guess, isLost, isOver, isWon, LETTERS, masked, MAX_WRONG, reveal, start, wrong, wrongLetters, type HangmanState, type HangmanWord } from "./core"
import { randomSeed, seeded } from "../lib/rng"
import { readJSON, writeJSON } from "../lib/store"
import { formatLong, isToday, mark } from "../lib/daily"
import { gameEvent } from "../lib/game-events"
import { gameProgress, newAttempt } from "../lib/stats"

export interface HangmanDaily { game: string; date: string; word: HangmanWord }
interface Saved { v: 1; state: HangmanState }
const BANK = "/puzzles/hangman/all.json"

export class HangmanGame {
  private state!: HangmanState
  private sentFirst = false
  private bank: HangmanWord[] | null = null
  private readonly status = document.querySelector<HTMLElement>("[data-status]")
  private readonly toolbar = document.querySelector<HTMLElement>("[data-toolbar]")
  private readonly keys: HTMLElement
  private readonly wordEl: HTMLElement
  private readonly themeEl: HTMLElement
  private readonly wrongEl: HTMLElement
  private readonly figure: SVGElement

  constructor(private readonly el: HTMLElement, private readonly daily?: HangmanDaily) {
    this.keys = el.querySelector<HTMLElement>("[data-hm-keys]")!
    this.wordEl = el.querySelector<HTMLElement>("[data-hm-word]")!
    this.themeEl = el.querySelector<HTMLElement>("[data-hm-theme]")!
    this.wrongEl = el.querySelector<HTMLElement>("[data-hm-wrong]")!
    this.figure = el.querySelector<SVGElement>("[data-hm-figure]")!
    const served: HangmanWord = daily?.word ?? { t: el.dataset.theme ?? "", w: el.dataset.word ?? "WORD" }
    const saved = readJSON<Saved>(this.saveKey)
    if (saved?.v === 1 && saved.state?.word && (!daily || saved.state.word === served.w)) {
      this.state = saved.state
      this.say(isOver(this.state) ? (isWon(this.state) ? "Welcome back. You got this one. Choose New word for another." : `Welcome back. That word was ${this.state.word}. Choose New word for another.`) : "Welcome back. Your word is where you left it.")
    } else {
      this.state = { ...start(served, randomSeed()), attempt: newAttempt() }
    }
    this.wire()
    this.render()
  }

  private get saveKey(): string { return this.daily ? `dt:save:daily:${this.daily.game}:${this.daily.date}:v1` : "dt:save:hangman:v1" }
  private get ruleset(): string { return "hangman:classic" }

  tryLetter(letter: string): void {
    const s = this.state
    const l = letter.toUpperCase()
    if (isOver(s)) return this.say(isWon(s) ? "You got it. Choose New word for another." : `The word was ${s.word}. Choose New word for another.`)
    if (s.guesses.includes(l)) return this.say(`You've tried ${l} already.`)
    const next = guess(s, l)
    if (next === s) return
    this.state = next
    this.persist()
    this.render()
    if (!this.sentFirst) { this.sentFirst = true; gameEvent({ type: "first_move", ruleset: this.ruleset, daily: !!this.daily }) }
    const hit = s.word.includes(l)
    const count = s.word.split("").filter((x) => x === l).length
    if (isWon(next)) {
      gameEvent({ type: "end", ruleset: this.ruleset, outcome: "win", moves: next.guesses.length, undos: 0, hints: next.hints, deal: next.word, daily: !!this.daily })
      if (this.daily) mark(this.daily.game, this.daily.date, "won")
      return this.say(this.daily ? (isToday(this.daily.date) ? `You got it: ${next.word}! Come back tomorrow for a new word.` : `You got the word for ${formatLong(this.daily.date)}: ${next.word}!`) : `You got it: ${next.word}, with ${wrong(next)} wrong guess${wrong(next) === 1 ? "" : "es"}.`)
    }
    if (isLost(next)) {
      gameEvent({ type: "end", ruleset: this.ruleset, outcome: "loss", moves: next.guesses.length, undos: 0, hints: next.hints, deal: next.word, daily: !!this.daily })
      if (this.daily) mark(this.daily.game, this.daily.date, "played")
      return this.say(`No ${l}. That was the sixth wrong guess: the word was ${next.word}. Choose New word to try another.`)
    }
    if (this.daily) mark(this.daily.game, this.daily.date, "played")
    const left = MAX_WRONG - wrong(next)
    this.say(hit ? `Yes, ${count === 1 ? "one" : count} ${l}${count === 1 ? "" : "s"}. ${this.wordWords(next)}` : `No ${l}. ${left} wrong guess${left === 1 ? "" : "es"} left.`)
  }

  private wordWords(s: HangmanState): string {
    return `The word so far: ${masked(s).map((l) => l || "blank").join(", ")}.`
  }

  showHint(): void {
    const s = this.state
    if (isOver(s)) return this.say("This word is finished. Choose New word for another.")
    const next = reveal(s)
    if (next === s) return
    const l = next.guesses[next.guesses.length - 1]!
    this.state = next
    this.persist()
    this.render()
    if (isWon(next)) {
      gameEvent({ type: "end", ruleset: this.ruleset, outcome: "win", moves: next.guesses.length, undos: 0, hints: next.hints, deal: next.word, daily: !!this.daily })
      if (this.daily) mark(this.daily.game, this.daily.date, "won")
      return this.say(`Hint: ${l}, and that was the last letter. The word is ${next.word}.`)
    }
    this.say(`Hint: the word has ${l === "A" || l === "E" || l === "I" || l === "O" || l === "U" ? "an" : "a"} ${l}. ${this.wordWords(next)}`)
  }

  async newWord(): Promise<void> {
    if (this.daily) return this.say("The daily word is the same for everyone. Come back tomorrow for a new one, or play as many as you like on the Hangman page.")
    if (this.sentFirst && !isOver(this.state)) gameEvent({ type: "end", ruleset: this.ruleset, outcome: "new_game", moves: this.state.guesses.length, undos: 0, hints: this.state.hints })
    this.sentFirst = false
    if (!this.bank) {
      try { this.bank = (await (await fetch(BANK)).json()) as HangmanWord[] } catch { this.bank = null }
    }
    const seed = randomSeed()
    const pool = (this.bank ?? []).filter((w) => w.w !== this.state.word)
    const pick = pool.length ? pool[Math.floor(seeded(seed)() * pool.length)]! : { t: this.state.theme, w: this.state.word }
    this.state = { ...start(pick, seed), attempt: newAttempt() }
    this.persist()
    this.render()
    this.say(`New word: ${this.state.word.length} letters, from ${this.state.theme}. Choose a letter.`)
  }

  private wire(): void {
    this.keys.addEventListener("click", (e) => {
      const l = (e.target as HTMLElement).closest<HTMLElement>("[data-letter]")?.dataset.letter
      if (l) this.tryLetter(l)
    })
    document.addEventListener("keydown", (e) => {
      const t = e.target as HTMLElement | null
      if (e.ctrlKey || e.metaKey || e.altKey || t?.matches("input, textarea, select")) return
      if (/^[a-zA-Z]$/.test(e.key)) {
        if (e.key === "h" || e.key === "H") { /* H is a letter here, not the hint key */ }
        e.preventDefault()
        this.tryLetter(e.key)
        this.keys.querySelector<HTMLElement>(`[data-letter="${e.key.toUpperCase()}"]`)?.focus({ preventScroll: true })
      }
    })
    this.toolbar?.addEventListener("click", (e) => {
      const act = (e.target as HTMLElement).closest<HTMLElement>("[data-act]")?.dataset.act
      if (act === "hint") this.showHint()
      else if (act === "new") void this.newWord()
    })
  }

  private say(msg: string): void { if (this.status) this.status.textContent = msg }
  private persist(): void { writeJSON(this.saveKey, { v: 1, state: this.state } satisfies Saved) }

  render(): void {
    const s = this.state
    const won = isWon(s), lost = isLost(s)
    gameProgress({ key: this.saveKey, ruleset: this.ruleset, id: `${s.seed}:${s.word}`, started: s.guesses.length > 0, won })
    this.el.classList.toggle("is-won", won)
    this.el.classList.toggle("is-lost", lost)
    this.themeEl.textContent = s.theme
    const n = wrong(s)
    this.wrongEl.textContent = `${n} of ${MAX_WRONG}`
    this.figure.querySelectorAll<SVGElement>("[data-part]").forEach((p) => { p.classList.toggle("is-shown", Number(p.dataset.part) <= n) })
    this.figure.setAttribute("aria-label", `${n} of ${MAX_WRONG} wrong guesses${n ? `: ${wrongLetters(s).join(", ")}` : ""}.`)
    const shown = masked(s)
    this.wordEl.innerHTML = s.word.split("").map((l, i) => `<span class="hm-letter${shown[i] ? " is-shown" : lost ? " is-missed" : ""}">${shown[i] || (lost ? l : "")}</span>`).join("")
    this.wordEl.setAttribute("aria-label", `The word, ${s.word.length} letters: ${shown.map((l) => l || "blank").join(", ")}.`)
    this.keys.querySelectorAll<HTMLButtonElement>("[data-letter]").forEach((b) => {
      const l = b.dataset.letter!
      const tried = s.guesses.includes(l)
      b.disabled = tried || isOver(s)
      b.className = `btn hm-key${tried ? (s.word.includes(l) ? " is-hit" : " is-miss") : ""}`
      b.setAttribute("aria-label", tried ? `${l}, ${s.word.includes(l) ? "in the word" : "not in the word"}` : l)
    })
  }
}

export const KEY_ROWS = [LETTERS.slice(0, 9), LETTERS.slice(9, 18), LETTERS.slice(18)]
